import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { INSPECT } from '@/constants/testIds';
import { toast } from 'sonner';
import { AlertTriangle, Check, Radar, Shield, ShieldAlert, ShieldCheck } from 'lucide-react';

const VERDICT_META = {
  HEALTHY:            { color: '#38BDF8', icon: ShieldCheck },
  OK:                 { color: '#38BDF8', icon: Shield },
  WEAK:               { color: '#FBBF24', icon: ShieldAlert },
  HARDENING_NEEDED:   { color: '#EF4444', icon: AlertTriangle },
};

const Panel = ({ title, children, right }) => (
  <div className="border border-[#3F3F46] bg-[#18181B]">
    <div className="flex items-center justify-between border-b border-[#3F3F46] px-4 py-2">
      <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500">// {title}</div>
      {right}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const KV = ({ k, v }) => (
  <div className="flex items-start justify-between gap-4 py-1 border-b border-[#27272A] last:border-0">
    <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 whitespace-nowrap">{k}</div>
    <div className="font-mono text-xs text-white text-right break-all">{v || '—'}</div>
  </div>
);

const ListBlock = ({ items, empty = '—' }) => {
  if (!items?.length) return <div className="text-xs text-gray-600">{empty}</div>;
  return (
    <ul className="space-y-1">
      {items.map((it, i) => (
        <li key={i} className="font-mono text-xs text-gray-300 break-all">
          <span className="text-gray-600 mr-2">&gt;</span>{it}
        </li>
      ))}
    </ul>
  );
};

export const InspectPage = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const isPro = user?.subscription_status === 'active' || user?.subscription_status === 'trialing';

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get('/tools/inspect/history');
      setHistory(data);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (isPro) loadHistory();
  }, [isPro, loadHistory]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!target.trim()) return;
    setBusy(true);
    setReport(null);
    try {
      const { data } = await api.post('/tools/inspect', { target: target.trim() });
      setReport(data);
      loadHistory();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402) {
        toast.error('PRO_REQUIRED');
        nav('/pricing');
        return;
      }
      toast.error(detail || 'Inspection failed');
    } finally {
      setBusy(false);
    }
  };

  if (!isPro) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-8 pt-16 pb-24">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#38BDF8] mb-3">
          // ACCESS_GATE
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tighter mb-6">
          URL Inspector<span className="caelix-caret"></span>
        </h1>
        <div data-testid={INSPECT.upgradeGate} className="c-card">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" strokeWidth={2.5} />
            <div>
              <div className="text-white text-sm uppercase tracking-widest">PRO_TIER_REQUIRED</div>
              <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                The URL/Domain Inspector is a Pro tool. Upgrade your clearance to run WHOIS,
                DNS enumeration, certificate transparency lookups, and reputation scoring on
                any target — all wired into your operator dashboard.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Link to="/pricing" className="c-btn-primary text-xs">UPGRADE_CLEARANCE</Link>
            <Link to="/pro/manage" className="c-btn-secondary text-xs">BACK_TO_MANAGE</Link>
          </div>
        </div>
      </div>
    );
  }

  const verdict = report?.reputation?.verdict;
  const vMeta = verdict ? VERDICT_META[verdict] : null;
  const VIcon = vMeta?.icon || Radar;

  return (
    <div data-testid={INSPECT.container} className="max-w-6xl mx-auto px-6 md:px-8 pt-10 pb-24">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#38BDF8] mb-3">
        // URL_INSPECTOR
      </div>
      <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tighter mb-6">
        Target Inspection<span className="caelix-caret"></span>
      </h1>
      <p className="text-xs text-gray-500 mb-6 max-w-2xl">
        &gt; Enter a domain or URL. Caelix will resolve DNS, pull WHOIS, scan certificate transparency logs, probe HTTP headers, and score reputation signals.
      </p>

      <form onSubmit={submit} className="c-card mb-8">
        <label className="c-label" htmlFor="inspect-target">Target Domain or URL</label>
        <div className="flex gap-2 items-stretch">
          <span className="text-[#38BDF8] font-bold self-center pl-1 pr-1">&gt;</span>
          <input
            id="inspect-target"
            data-testid={INSPECT.input}
            type="text"
            required
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="example.com or https://example.com/path"
            className="c-input flex-1"
          />
          <button
            data-testid={INSPECT.submit}
            type="submit"
            disabled={busy}
            className="c-btn-primary text-xs"
          >
            {busy ? 'RUNNING…' : 'INSPECT'}
          </button>
        </div>
      </form>

      {busy && (
        <div className="c-card mb-8">
          <div className="text-xs text-gray-400 tracking-widest uppercase">
            RESOLVING_DNS · WHOIS · CT_LOGS · HTTP<span className="caelix-dots"></span>
          </div>
        </div>
      )}

      {report && (
        <div data-testid={INSPECT.report} className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
          <div className="c-card lg:col-span-3 flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="c-label">TARGET</div>
              <div className="text-xl font-bold text-white break-all">{report.hostname}</div>
              <div className="text-[10px] text-gray-600 tracking-widest uppercase mt-1">
                REPORT_ID :: {report.generated_at}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <VIcon className="w-6 h-6" strokeWidth={2.5} style={{ color: vMeta?.color || '#a1a1aa' }} />
              <div>
                <div className="c-label">VERDICT</div>
                <div
                  data-testid={INSPECT.verdict}
                  className="text-lg font-bold uppercase tracking-wider"
                  style={{ color: vMeta?.color || '#fff' }}
                >
                  {verdict}
                </div>
                <div data-testid={INSPECT.score} className="text-[10px] text-gray-500 tracking-widest uppercase">
                  SCORE :: {report.reputation.score}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <Panel title="REPUTATION_SIGNALS">
              <div className="flex flex-wrap gap-2">
                {report.reputation.signals.map((s) => (
                  <span
                    key={s}
                    className={`c-badge ${
                      s.endsWith('_MISSING') || s.endsWith('_NEEDED')
                        ? 'c-badge--warn'
                        : 'c-badge--active'
                    }`}
                  >
                    {s.endsWith('_MISSING') || s.endsWith('_NEEDED') ? '' : '✓ '}
                    {s}
                  </span>
                ))}
              </div>
            </Panel>
          </div>

          <Panel title="DNS_RECORDS">
            {['A', 'AAAA', 'MX', 'NS', 'TXT', 'CNAME'].map((k) => (
              <div key={k} className="mb-3 last:mb-0">
                <div className="text-[10px] text-gray-500 tracking-widest uppercase mb-1">{k}</div>
                <ListBlock items={report.dns?.[k]} empty="&gt; none" />
              </div>
            ))}
          </Panel>

          <Panel title="WHOIS">
            {report.whois && Object.keys(report.whois).length ? (
              Object.entries(report.whois).map(([k, v]) => (
                <KV key={k} k={k.replace(/_/g, ' ')} v={Array.isArray(v) ? v.join(', ') : String(v)} />
              ))
            ) : (
              <div className="text-xs text-gray-500">&gt; no whois data</div>
            )}
          </Panel>

          <Panel title="HTTP_PROBE">
            <KV k="URL" v={report.http?.url} />
            <KV k="STATUS" v={report.http?.status} />
            <KV k="FINAL_URL" v={report.http?.final_url} />
            <KV k="SERVER" v={report.http?.headers?.Server || report.http?.headers?.server} />
            <KV k="TECH" v={(report.http?.tech || []).join(', ')} />
            <KV k="REDIRECTS" v={(report.http?.redirect_chain || []).length} />
          </Panel>

          <Panel
            title="SSL_CERT_TRANSPARENCY"
            right={<span className="text-[10px] text-gray-600">{report.ssl_certs?.length || 0} entries</span>}
          >
            {report.ssl_certs?.length ? (
              <div className="max-h-64 overflow-auto">
                {report.ssl_certs.map((c, i) => (
                  <div key={i} className="border-b border-[#27272A] last:border-0 py-2">
                    <div className="text-[10px] tracking-widest uppercase text-gray-500 truncate">
                      {c.issuer}
                    </div>
                    <div className="text-xs text-gray-300 font-mono break-all">
                      {(c.names || []).slice(0, 3).join(', ')}
                    </div>
                    <div className="text-[10px] text-gray-600 tracking-widest uppercase">
                      {c.not_before} → {c.not_after}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-gray-500">&gt; no CT log entries</div>
            )}
          </Panel>

          <Panel title="IP_DETAILS">
            {report.ip_details?.length ? (
              report.ip_details.map((ip) => (
                <div key={ip.ip} className="mb-2 last:mb-0">
                  <div className="font-mono text-sm text-white">{ip.ip}</div>
                  <div className="text-[10px] text-gray-500 tracking-widest uppercase">
                    RDNS :: {ip.reverse_dns || 'none'}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-xs text-gray-500">&gt; no ip details</div>
            )}
          </Panel>
        </div>
      )}

      {history.length > 0 && (
        <Panel title="RECENT_INSPECTIONS">
          <div data-testid={INSPECT.historyList}>
            {history.map((h) => (
              <button
                key={h.id}
                data-testid={INSPECT.historyItem}
                onClick={() => { setTarget(h.target); submit(); }}
                className="w-full text-left grid grid-cols-[1.4fr_0.6fr_0.6fr_0.6fr] items-center py-2 border-b border-[#27272A] last:border-0 hover:bg-[#27272A] transition-colors"
              >
                <div className="font-mono text-xs text-white truncate">{h.hostname}</div>
                <div className="font-mono text-[10px] text-gray-500 uppercase tracking-widest">{h.reputation_verdict}</div>
                <div className="font-mono text-[10px] text-gray-500">SCORE {h.reputation_score}</div>
                <div className="font-mono text-[10px] text-gray-600 text-right truncate">
                  {new Date(h.generated_at).toISOString().slice(0, 16).replace('T', ' ')}
                </div>
              </button>
            ))}
          </div>
        </Panel>
      )}

      {!report && !busy && history.length === 0 && (
        <div className="c-card">
          <div className="flex items-center gap-3">
            <Check className="w-4 h-4 text-[#38BDF8]" strokeWidth={2.5} />
            <div className="text-xs text-gray-500 tracking-widest uppercase">
              READY · enter a target above to run your first inspection
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InspectPage;
